import { SubscriptionStatus, UserSubscription } from 'types/subscription';

export const STRIPE_ENDED_STATUSES = [
  'canceled',
  'unpaid',
  'incomplete_expired',
] as const;

export type StripeEndedStatus = typeof STRIPE_ENDED_STATUSES[number];

/** Sticky + profile status billing-attention reasons, highest priority first. */
export const BILLING_ATTENTION_REASONS = [
  'unpaid',
  'past_due',
  'overdue_invoice',
] as const;

export type BillingAttentionReason = typeof BILLING_ATTENTION_REASONS[number];

function isSendInvoiceCollection(
  subscription?: UserSubscription | null,
): boolean {
  return subscription?.billingCollectionMethod === 'send_invoice';
}

/**
 * Invoice billing past due: Stripe marks the subscription `past_due` when a
 * `send_invoice` invoice is unpaid after its due date.
 */
export function isOverdueInvoice(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  const billingStatus = subscription.billingSubscriptionStatus?.toLowerCase();
  if (billingStatus === 'overdue_invoice') return true;
  return billingStatus === 'past_due' && isSendInvoiceCollection(subscription);
}

export function isPaidPlanTrialPeriod(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription?.subscriptionPlan) return false;
  if (subscription.status === SubscriptionStatus.TRIAL_EXPIRED) return false;

  if (subscription.planId === SubscriptionStatus.TRIAL) return false;
  if (subscription.status === SubscriptionStatus.TRIAL) return true;
  return false;
}

export function isStripeSubscriptionFullyEnded(
  subscription?: UserSubscription | null,
): boolean {
  const status = subscription?.billingSubscriptionStatus;
  if (!status) return false;
  return (STRIPE_ENDED_STATUSES as readonly string[]).includes(status);
}

export function isStripeAccessPeriodStatus(
  subscription?: UserSubscription | null,
): boolean {
  const status = subscription?.billingSubscriptionStatus;
  return status === 'active' || status === 'trialing';
}

export function isStripeCancelAtPeriodEnd(
  subscription?: UserSubscription | null,
): boolean {
  return Boolean(
    subscription?.billingCancelAtPeriodEnd === true &&
      isStripeAccessPeriodStatus(subscription),
  );
}

/** App-level cancelled when Stripe has not fully ended the subscription. */
export function isLegacyAppCancelled(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isStripeSubscriptionFullyEnded(subscription)) return false;
  return (
    subscription.status === SubscriptionStatus.CANCELLED &&
    subscription.subscriptionId !== SubscriptionStatus.MIGRATED
  );
}

/**
 * User canceled but still has access until period end (no renewal).
 * Billing attention for routine payment issues is suppressed in this state
 * (overdue invoice / pause / unpaid are handled separately).
 */
export function isUserScheduledCancel(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isLegacyAppCancelled(subscription)) return true;
  return (
    subscription.billingCancelAtPeriodEnd === true &&
    !isStripeSubscriptionFullyEnded(subscription)
  );
}

/**
 * Highest-priority billing attention for sticky + profile status banners.
 * Driven by billingSubscriptionStatus + billingCollectionMethod.
 * Suppressed for scheduled cancel except unpaid, pause, and overdue invoice.
 */
export function getBillingAttentionReason(
  subscription?: UserSubscription | null,
): BillingAttentionReason | null {
  if (!subscription) return null;
  if (subscription.status === SubscriptionStatus.TRIAL_EXPIRED) return null;

  const billingStatus = subscription.billingSubscriptionStatus?.toLowerCase();

  if (billingStatus === 'unpaid') return 'unpaid';

  if (isOverdueInvoice(subscription)) return 'overdue_invoice';

  if (isUserScheduledCancel(subscription)) return null;

  if (billingStatus === 'past_due') return 'past_due';

  return null;
}

function isAccessEndDatePassed(
  subscription?: UserSubscription | null,
  now = new Date(),
): boolean {
  const endIso =
    subscription?.endDate ||
    subscription?.billingEndedAt ||
    subscription?.billingCancelAt ||
    subscription?.billingCurrentPeriodEnd;

  if (!endIso) return false;
  const endDate = new Date(endIso);
  if (Number.isNaN(endDate.getTime())) return false;
  return now.getTime() > endDate.getTime();
}

/**
 * CANCELLED with no remaining access: Stripe ended, unpaid revoke, or period end passed.
 * CANCELLED still inside the paid/trialing period is not over.
 */
export function isCancelledSubscriptionOver(
  subscription?: UserSubscription | null,
  now = new Date(),
): boolean {
  if (!subscription) return false;
  if (subscription.status !== SubscriptionStatus.CANCELLED) return false;
  if (isStripeAccessPeriodStatus(subscription)) return false;
  if (isStripeSubscriptionFullyEnded(subscription)) return true;
  if (isCancelledWithoutRemainingAccess(subscription)) return true;
  return isAccessEndDatePassed(subscription, now);
}

/** Create-community is blocked until the user chooses a new plan. */
export function isCreateCommunityBlockedByExpiredSubscription(
  subscription?: UserSubscription | null,
  now = new Date(),
): boolean {
  if (!subscription) return true;
  if (subscription.status === SubscriptionStatus.TRIAL_EXPIRED) return true;
  return isCancelledSubscriptionOver(subscription, now);
}

/**
 * True when access was revoked for non-payment (no remaining paid period).
 * "Valid until" should show "-" in this case — not a period-end date.
 */
export function isCancelledWithoutRemainingAccess(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (subscription.status !== SubscriptionStatus.CANCELLED) return false;

  if (
    subscription.pausedForNonPaymentAt ||
    subscription.cancelledForNonPaymentAt
  ) {
    return true;
  }

  // CANCELLED with no period-end fields → nothing left to show as valid-until.
  if (!subscription.billingCurrentPeriodEnd && !subscription.endDate) {
    return true;
  }

  return false;
}

/**
 * Cancelled after unpaid/overdue invoice: resubscribe checkout must use
 * direct payment. Invoice option stays visible but disabled.
 * Once they reactivate on card, they are a normal subscriber again.
 */
export function mustResubscribeWithDirectPayment(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (subscription.status !== SubscriptionStatus.CANCELLED) return false;

  if (
    subscription.pausedForNonPaymentAt ||
    subscription.cancelledForNonPaymentAt
  ) {
    return true;
  }

  const billingStatus = subscription.billingSubscriptionStatus?.toLowerCase();
  return billingStatus === 'overdue_invoice' || billingStatus === 'unpaid';
}

export class InvoiceBillingNotAllowedError extends Error {
  constructor(
    message = 'Invoice billing is not available after unpaid cancellation',
  ) {
    super(message);
    this.name = 'InvoiceBillingNotAllowedError';
  }
}

/** Throws if the current subscription must resubscribe with direct payment only. */
export function assertCanCreateInvoiceSubscription(
  subscription?: UserSubscription | null,
): void {
  if (mustResubscribeWithDirectPayment(subscription)) {
    throw new InvoiceBillingNotAllowedError();
  }
}

export function getStripeSubscriptionEndIso(
  subscription?: UserSubscription | null,
): string | null | undefined {
  const sub = subscription;
  if (!sub) return undefined;

  // Cancelled for non-payment / no remaining access: never surface a date.
  if (isCancelledWithoutRemainingAccess(sub)) {
    return undefined;
  }

  if (sub.status === SubscriptionStatus.TRIAL_EXPIRED) {
    return (
      sub.endDate ||
      sub.billingEndedAt ||
      sub.billingCurrentPeriodEnd ||
      undefined
    );
  }

  if (isStripeSubscriptionFullyEnded(sub)) {
    return (
      sub.billingEndedAt ||
      sub.billingCancelAt ||
      sub.billingCurrentPeriodEnd ||
      sub.endDate ||
      undefined
    );
  }

  if (isStripeCancelAtPeriodEnd(sub)) {
    return sub.billingCancelAt || sub.billingCurrentPeriodEnd || undefined;
  }

  return sub.billingCurrentPeriodEnd || sub.endDate || undefined;
}

export function isStripeNonRenewingActive(
  subscription?: UserSubscription | null,
  isPaidActive = false,
): boolean {
  return Boolean(isPaidActive && isStripeCancelAtPeriodEnd(subscription));
}

export function isChfSubscriptionCurrency(
  subscription?: UserSubscription | null,
): boolean {
  const currency = (
    subscription?.billingPriceCurrency ||
    subscription?.subscriptionPlan?.currency ||
    ''
  ).toUpperCase();
  return currency === 'CHF';
}

export function isInvoiceBillingLayout(
  subscription?: UserSubscription | null,
  showBillingPaymentDetails = false,
): boolean {
  if (!showBillingPaymentDetails || !subscription) return false;
  if (subscription.billingCollectionMethod === 'send_invoice') return true;
  if (subscription.enterpriseInvoiceRequest?.contactEmail) return true;
  return false;
}

export function isEnterprisePaidPlan(
  subscription?: UserSubscription | null,
  isPaidActive = false,
): boolean {
  if (!isPaidActive || !subscription) return false;
  const label = `${subscription.subscriptionPlan?.productName ?? ''} ${
    subscription.subscriptionPlan?.name ?? ''
  }`;
  if (/enterprise/i.test(label)) return true;
  if (
    subscription.enterpriseInvoiceRequest ||
    subscription.enterpriseInvoiceRequestId
  ) {
    return true;
  }
  return false;
}
